import { FormProvider, useForm } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { floor } from 'lodash';
import { simulationFormSchema, type SimulationFormData } from '.';
import { StaticModal, createModal } from '@nexelem/modals';
import { SimulationLotMain } from './components/SimulationLot';
import { useGetGraphneSimulationLots, useSortConfig } from '../../hooks';
import {
  SimulationModalFilters,
  defaultSimulationFilters,
  simulationFiltersMapper,
} from './components/SimulationFilters';
import { SimulationGridHeader, SimulationEditHeader, SimulationEditFooter } from './components/SimulationForm';
import { useCustomTranslation } from '../../../../../../hooks/useCustomTranslation';

export const SimulationEditForm = () => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const [filters, setFilters] = useState(defaultSimulationFilters);
  const { sort, toggleSort, getSortOrder } = useSortConfig();
  const mappedFilters = simulationFiltersMapper(filters);

  const {
    data: lots = [],
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useGetGraphneSimulationLots(mappedFilters, sort);

  const defaultValues = useMemo(() => {
    const values: SimulationFormData = {};
    lots.forEach((task) => {
      values[task.id] = {
        amount: floor(task.progress.totalAmount.amount, 2),
        priority: task.priority,
        delete: false,
        technology: task.qapsSimulationData?.recipeVersion,
      };
    });
    return values;
  }, [lots]);

  const form = useForm<SimulationFormData>({
    resolver: zodResolver(simulationFormSchema),
    defaultValues: {},
    mode: 'onBlur',
  });

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues, { keepDirtyValues: true });
    }
  }, [defaultValues, form]);

  return (
    <FormProvider {...form}>
      <StaticModal fullScreen onClose={closeSimulationEditForm}>
        <SimulationEditHeader simulationFilters={<SimulationModalFilters filters={filters} onChange={setFilters} />} />
        <StaticModal.Content className="border-t border-border-primary px-8 py-3">
          <SimulationGridHeader getSortOrder={getSortOrder} onSort={toggleSort} />
          {lots.length === 0 && !isFetching ? (
            <div className="flex w-full justify-center py-8">
              <p className="text-s16-sb">{tWrapper('noResultsMessage')}</p>
            </div>
          ) : (
            <SimulationLotMain
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isLoading={isLoading}
              tasks={lots}
            />
          )}
        </StaticModal.Content>
        <SimulationEditFooter taskNumber={lots.length} />
      </StaticModal>
    </FormProvider>
  );
};

export const { openSimulationEditForm, closeSimulationEditForm } = createModal(SimulationEditForm, {
  name: 'SimulationEditForm',
});
